// css reset
import { LicenseManager } from "ag-grid-enterprise";
import { AuthRoleUpdater } from "components/auth/AuthRoleUpdater";
import DialogContainer from "components/dialog/controller/DialogContainer";
import { DialogProvider } from "components/dialog/controller/DialogProvider";
import Layout from "components/layout/Layout";
import Loading from "components/loading/Loading";
import { Alert } from "components/modal/Alert";
import { Confirm } from "components/modal/Confirm";
import { Toast } from "components/toast/Toast";
import { ListMoreMenu } from "components/tooltip/ListMoreMenu/ListMoreMenu";
import ErrorBoundary from "ErrorBoundary";
import useUser from "hooks/useUser";
import { Suspense, useEffect, useState } from "react";
import { CookiesProvider } from "react-cookie";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { HashRouter, Route, Routes } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { routes } from "router";
import { PrivateRoute } from "router/PrivateRoute";
import { SetupStyle } from "SetupStyle";
import useAlertStore from "store/useAlertStore";
import useAuthStore from "store/useAuthStore";
import useErrorStore from "store/useErrorStore";
import useLoadingStore from "store/useLoadingStore";
import useMoreMenuStore from "store/useMoreMenuStore";
import useResizeGuideStore from "store/useResizeGuideStore";
import { Util } from "Util";
import "./animation.css";
import "./index.css";
import ChatbotPopup from "components/chatbot/ChatbotPopup";
import { Button } from "components/button/Button/Button";
import useChatbotStore from "store/chatbot/useChatbotStore";
import { FEATURES } from "const/feature/const";
import { Icons } from "contents/icons";
import { Icon } from "components/icon/Icon";
import { ChatbotButton } from "components/chatbot/chatbotButton/ChatbotButton";

function App() {
  // 로그인 상태 확인
  const { isLogin: isLoginCheck } = useUser();
  // 로그인 상태 저장
  const isLogin = useAuthStore((state) => state.isLogin);

  const { isChatbotOpen, setIsChatbotOpen } = useChatbotStore();
  const { completeLoadingState, loadingCount } = useLoadingStore();
  const { isAlert, isConfirm } = useAlertStore();
  const { isMoreMenuVisible } = useMoreMenuStore();
  const { resizeGuideLineVisible, lineX, lineY, lineDirection } =
    useResizeGuideStore();
  const [isLoadingOverlay, setIsLoadingOverlay] = useState(false);
  const randomLicenseKey = Util.getUniqueKey(36);
  LicenseManager.setLicenseKey(randomLicenseKey);
  const { errorCode } = useErrorStore();

  useEffect(() => {
    if (
      window.location.hash.includes("/project") ||
      !window.location.hash.includes("/flows")
    ) {
      setIsLoadingOverlay(false);
    } else {
      setIsLoadingOverlay(loadingCount > 0);
    }
  }, [loadingCount]);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        if (mounted) {
          await isLoginCheck();
        }
      } catch (error) {
        console.error("Login check failed:", error);
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, [isLoginCheck]);

  return (
    <CookiesProvider>
      <HashRouter>
        <DndProvider backend={HTML5Backend}>
          <DialogProvider>
            <SetupStyle />
            <AuthRoleUpdater>
              <div
                className={`App ${isLoadingOverlay ? "loading-overlay" : ""}`}
                style={
                  resizeGuideLineVisible
                    ? {
                        cursor:
                          lineDirection === "horizon"
                            ? "ns-resize"
                            : "ew-resize",
                      }
                    : {}
                }
              >
                <Layout>
                  <ErrorBoundary>
                    <Suspense>
                      <Routes>
                        {routes.common.map((item, index) => (
                          <Route
                            key={index}
                            path={item.path}
                            element={
                              <PrivateRoute
                                key={index}
                                path={item.path}
                                fallback={() => <p>FallBack</p>}
                                isPermission
                                errorCode={errorCode}
                                component={item.component}
                                isLogin={isLogin}
                                exact
                              />
                            }
                          />
                        ))}
                      </Routes>
                    </Suspense>

                    {/* 로딩창 */}
                    {(completeLoadingState || loadingCount > 0) && <Loading />}

                    {/* 리스트 more 메뉴 */}
                    {isMoreMenuVisible && <ListMoreMenu />}

                    {/* 요소 사이즈 조절시 표출되는 가이드라인 */}
                    {resizeGuideLineVisible && (
                      <span
                        id="resize-guide-line"
                        style={
                          lineDirection === "horizon"
                            ? {
                                top: lineY + "px",
                                left: "0",
                                width: "100%",
                                height: "0",
                                borderBottom:
                                  "0.3rem dashed var(--sec-orange-4)",
                              }
                            : {
                                top: "0",
                                left: lineX + "px",
                                width: "0",
                                height: "100%",
                                borderRight:
                                  "0.3rem dashed var(--sec-orange-4)",
                              }
                        }
                      />
                    )}

                    <DialogContainer />

                    {FEATURES.S_CORE_MODE && (
                      <>
                        <ChatbotPopup />
                        {isLogin && <ChatbotButton />}
                      </>
                    )}

                    {/* 메시지창 */}
                    <TransitionGroup>
                      <Toast />
                    </TransitionGroup>

                    {/* 알림창 */}
                    <CSSTransition
                      in={isAlert}
                      timeout={500}
                      classNames="modal"
                      unmountOnExit
                    >
                      <Alert />
                    </CSSTransition>

                    {/* 알림창 */}
                    <CSSTransition
                      in={isConfirm}
                      timeout={500}
                      classNames="modal"
                      unmountOnExit
                    >
                      <Confirm />
                    </CSSTransition>
                  </ErrorBoundary>
                </Layout>
              </div>
            </AuthRoleUpdater>
          </DialogProvider>
        </DndProvider>
      </HashRouter>
    </CookiesProvider>
  );
}

export default App;
